package com.crowpay.views.components.project.work.scope

import com.crowpay.LineItemState
import com.crowpay.ScopeViewInterface
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.requiresAction
import com.crowpay.sdk.UnreadHandler
import com.crowpay.utils.AdjustedLineItem
import com.crowpay.utils.maxOfNotNullOrNull
import com.crowpay.utils.renderPercent
import com.crowpay.views.components.actionIcon
import com.crowpay.views.components.expandAndMarkAsReadAction
import com.crowpay.views.components.expandIcon
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.priceWithChange
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.components.space
import com.crowpay.views.components.unreadIconVisible
import com.crowpay.views.screens.common.ActionImportance
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.*
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*

data class WorkItemWeights(val narrow: Boolean = false) {
    val nameWeight = if (narrow) 4f else 5.5f
    val amountWeight = 1.2f
//    val paidWeight = 1.2f
    val percentCompleteWeight = if (narrow) 1f else 0.9f
    val statusWeight = if (narrow) 1.5f else 1.7f

    val infoWeight get() = amountWeight + percentCompleteWeight + statusWeight
}

fun ViewWriter.workItemTableHeader(showNameHeader: Boolean): ViewModifiable {
    return sizeConstraints(height = 3.rem) - row {
        spacing = AppDimensions.buttonSpacing
        val weights = WorkItemWeights(viewIsNarrow)
        if (showNameHeader)
            weight(weights.nameWeight) - row {
                spacing = AppDimensions.buttonSpacing
                space(AppDimensions.sectionIndent)
                centered - bodyBold("Work Item")
            }
        else
            weight(weights.nameWeight) - space()

        weight(weights.amountWeight) - centered - bodyBold {
            align = Align.Center
            content = "Amount"
        }
//        weight(weights.paidWeight) - centered - bodyBold {
//            align = Align.Center
//            content = "Paid"
//        }
        weight(weights.percentCompleteWeight) - centered - bodyBold {
            align = Align.Center
            content = "% Complete"
        }
        weight(weights.statusWeight) - centered - bodyBold {
            align = Align.Center
            content = "Status"
        }
    }
}

fun ProjectView.workItemTableRow(
    expanded: Property<Boolean>,
    item: Readable<AdjustedLineItem>,
    writer: ViewWriter,
    unread: Readable<UnreadHandler>,
    topLevel: Boolean = true
): RView = with(writer) {
    expandButtonTheme
    button {
        spacing = 0.px
        if (topLevel) dynamicTheme {
            if (expanded()) SectionSemantic else null
        }

        sizeConstraints(height = 3.rem) - row {
            spacing = AppDimensions.buttonSpacing
            val weights = WorkItemWeights(viewIsNarrow)
            weight(weights.nameWeight) - row {
                sizeConstraints(width = AppDimensions.sectionIndent) - centered - row {
                    spacing = 0.px
                    expanding - space()
                    centered - unreadIconVisible { unread()() }
                }
                spacing = AppDimensions.expandButtonSpace
                centered - expandIcon(expanded)
                centered - actionIcon {
                    val line = item()
                    if (line.scopeSet == true) return@actionIcon null
                    maxOfNotNullOrNull(
                        workItemPermissions()[line._id]?.requiresAction(),
                        if (line.state == LineItemState.RequestingReview) ActionImportance.Important else null
                    )
                }
                centered - body {
                    dynamicTheme { if (item().cancelled) StrikeThroughSemantic else null }
                    ::content { item().name }
                }
                centered - smallBody { ::content { item().changeType?.postTense ?: "" } }
            }
            weight(weights.amountWeight) - centered - stack {
                centered - priceWithChange(
                    price = { item().wraps.price },
                    change = { item().wraps.let { it.price - it.originalPrice } }
                )
            }
            val paid = shared {
                val line = item()
                if (line.complete != null) return@shared line.wraps.price
                payAppItems()
                    .filter { it.lineItem == line._id && (it.pending || it.accepted == true) }
                    .sumOf { it.amount }
            }
//            weight(weights.paidWeight) - centered - body {
//                align = Align.Center
//                ::content { paid().renderDollars() }
//            }
            weight(weights.percentCompleteWeight) - centered - body {
                align = Align.Center
                ::content { (paid().toDouble() / item().wraps.price).renderPercent() }
            }
            weight(weights.statusWeight) - centered - body {
                fun AdjustedLineItem.status() = if (scopeSet == true) null else state
                dynamicTheme { item().status()?.let(::LineItemStateSemantic) }
                align = Align.Center
                ::content { item().status()?.displayName ?: "" }
            }
        }
        action = expandAndMarkAsReadAction(expanded, unread)
    }
}

fun ProjectView.workItemTableSummary(
    items: Readable<List<AdjustedLineItem>>,
    scopeView: Readable<ScopeViewInterface>,
    writer: ViewWriter
) = with(writer) {
    sizeConstraints(height = 3.5.rem) - row {
        spacing = AppDimensions.buttonSpacing
        val weights = WorkItemWeights(viewIsNarrow)
        weight(weights.nameWeight) - compact - row {
            space(AppDimensions.sectionIndent)
            colored(AppColors.secondary.main) - centered - scopeSetIcon {
                ::exists { scopeView().scopeSet }
            }
            centered - body {
                ::content {
                    if (scopeView().scopeSet) "Work Set Summary"
                    else "Work View Summary"
                }
            }
        }
        weight(weights.amountWeight) - centered - stack {
            centered - priceWithChange(
                price = { items().sumOf { it.wraps.price } },
                change = { items().sumOf { it.wraps.price - it.wraps.originalPrice } }
            )
        }
        val paid = shared {
            val lines = items()
            val draws = payAppItems()
            lines.sumOf { line ->
                if (line.complete != null) line.wraps.price
                else draws
                    .filter { it.lineItem == line._id && (it.pending || it.accepted == true) }
                    .sumOf { it.amount }
            }
        }
//        weight(weights.paidWeight) - centered - body {
//            align = Align.Center
//            ::content { paid().renderDollars() }
//        }
        weight(weights.percentCompleteWeight) - centered - body {
            align = Align.Center
            ::content {
                (paid().toDouble() / items().sumOf { it.wraps.price }).renderPercent()
            }
        }
        weight(weights.statusWeight) - centered - body {
            val status = shared {
                if (scopeView().scopeSet) items().firstOrNull()?.state
                else null
            }
            dynamicTheme { status()?.let(::LineItemStateSemantic) }
            align = Align.Center
            ::content { status()?.displayName ?: "" }
        }
    }
}
